/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import CardContent from "@mui/material/CardContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";

function PerorangDetail() {
  const [detailData, setDetailData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pegawaiInfo, setPegawaiInfo] = useState(null);
  const [openKegiatanModal, setOpenKegiatanModal] = useState(false);
  const [selectedKegiatan, setSelectedKegiatan] = useState([]);
  const [selectedLaporanTitle, setSelectedLaporanTitle] = useState("");
  const [loadingKegiatan, setLoadingKegiatan] = useState(false);
  const navigate = useNavigate();
  const { uid_RSS } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Ambil query parameters
  const tanggalAwal = searchParams.get("tanggal_awal");
  const tanggalAkhir = searchParams.get("tanggal_akhir");
  const nama = searchParams.get("nama");

  const fetchDetailData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}laporan-harian/pegawai/${uid_RSS}`,
        {
          params: {
            tanggal_awal: tanggalAwal,
            tanggal_akhir: tanggalAkhir,
          },
        }
      );

      if (response.data && response.data.length > 0) {
        setDetailData(response.data);
        setPegawaiInfo(response.data[0]);
      } else {
        setDetailData([]);
        swal({
          title: "Info",
          text: "Tidak ada data laporan harian untuk periode yang dipilih",
          icon: "info",
          button: "OK",
        });
      }
    } catch (error) {
      console.error("Error fetching detail data:", error);
      swal({
        title: "Error!",
        text: "Terjadi kesalahan saat mengambil data detail",
        icon: "error",
        button: "OK",
      });
      setDetailData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (uid_RSS && tanggalAwal && tanggalAkhir) {
      fetchDetailData();
    }
  }, [uid_RSS, tanggalAwal, tanggalAkhir]);

  const handleOpenKegiatanModal = (id_laporan, tanggal) => {
    setOpenKegiatanModal(true);
    setLoadingKegiatan(true);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle(
      `Kegiatan Laporan Harian - ${new Date(tanggal).toLocaleDateString(
        "id-ID"
      )}`
    );

    // Fetch kegiatan dari backend
    axios
      .get(`${API_ESA_OFFICE}laporan-harian/listkegiatan/${id_laporan}`)
      .then((res) => {
        setSelectedKegiatan(res.data.kegiatan || []);
        setLoadingKegiatan(false);
      })
      .catch((err) => {
        console.error("Error fetching kegiatan:", err);
        swal("Error", "Gagal mengambil data kegiatan", "error");
        setSelectedKegiatan([]);
        setLoadingKegiatan(false);
      });
  };

  const handleCloseKegiatanModal = () => {
    setOpenKegiatanModal(false);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle("");
    setLoadingKegiatan(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  const formatTanggal = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  const columns = [
    {
      Header: "No",
      accessor: "no",
      width: "6%",
      align: "center",
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: "Tanggal",
      accessor: "tanggal",
      width: "15%",
      Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="bold" color="text">
          {formatTanggal(value)}
        </MDTypography>
      ),
    },
    {
      Header: "Hari",
      accessor: "hari",
      width: "12%",
      Cell: ({ row }) => formatDate(row.original.tanggal).split(",")[0],
    },
    {
      Header: "Jumlah Kegiatan",
      accessor: "jumlah_kegiatan",
      width: "12%",
      align: "center",
      Cell: ({ value }) => (
        <Chip label={value || 0} color="info" size="small" variant="filled" />
      ),
    },
    {
      Header: "Aksi",
      accessor: "aksi",
      width: "15%",
      Cell: ({ row }) => (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() =>
            handleOpenKegiatanModal(
              row.original.id_laporan,
              row.original.tanggal
            )
          }
          sx={{ minWidth: "auto", px: 1 }}
        >
          <Icon sx={{ fontSize: 16 }}>visibility</Icon>
          &nbsp;Lihat Kegiatan
        </MDButton>
      ),
    },
  ];

  // Fungsi untuk menghitung statistik
  const calculateStats = () => {
    const stats = {
      totalLaporan: detailData.length,
      totalKegiatan: detailData.reduce(
        (sum, item) => sum + (item.jumlah_kegiatan || 0),
        0
      ),
      rataRataKegiatan:
        detailData.length > 0
          ? (
              detailData.reduce(
                (sum, item) => sum + (item.jumlah_kegiatan || 0),
                0
              ) / detailData.length
            ).toFixed(2)
          : 0,
    };
    return stats;
  };

  const stats = calculateStats();

  const handleExportExcel = () => {
    if (!detailData.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = ["No", "Tanggal", "Hari", "Jumlah Kegiatan"];

    let tableRows = "";
    detailData.forEach((row, index) => {
      const hari = formatDate(row.tanggal).split(",")[0];
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${formatTanggal(row.tanggal)}</td>
          <td>${hari}</td>
          <td>${row.jumlah_kegiatan || 0}</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Detail Laporan Harian - ${nama} (${tanggalAwal} s/d ${tanggalAkhir})</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Detail Laporan Harian Pegawai: ${nama}</h2>
          <h3>Periode: ${formatTanggal(tanggalAwal)} s/d ${formatTanggal(
      tanggalAkhir
    )}</h3>
          <h4>Statistik:</h4>
          <ul>
            <li>Total Laporan: ${stats.totalLaporan}</li>
            <li>Total Kegiatan: ${stats.totalKegiatan}</li>
            <li>Rata-rata Kegiatan per Laporan: ${stats.rataRataKegiatan}</li>
          </ul>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(
      blob,
      `detail-laporan-harian-${nama}-${tanggalAwal}-${tanggalAkhir}.xls`
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <MDTypography variant="h4">
                Detail Laporan Harian - {nama}
              </MDTypography>
            </Grid>
            <Grid item>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={handleExportExcel}
                  disabled={!detailData.length}
                >
                  <Icon>description</Icon>
                  &nbsp;Export Excel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => navigate(-1)}
                >
                  <Icon>arrow_back</Icon>&nbsp;Kembali
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Statistik */}
        <MDBox mb={3}>
          <MDTypography variant="h6" color="text" mb={2}>
            Statistik Laporan Harian
          </MDTypography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Chip
                icon={<Icon>description</Icon>}
                label={`Total Laporan: ${stats.totalLaporan}`}
                color="primary"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>list</Icon>}
                label={`Total Kegiatan: ${stats.totalKegiatan}`}
                color="info"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>trending_up</Icon>}
                label={`Rata-rata: ${stats.rataRataKegiatan} kegiatan/laporan`}
                color="success"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
          </Grid>
          <MDBox mt={1}>
            <MDTypography variant="caption" color="text.secondary">
              Periode: {formatTanggal(tanggalAwal)} s/d{" "}
              {formatTanggal(tanggalAkhir)}
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox mb={2}>
          <MDTypography variant="h6" color="text">
            Data Laporan Harian: {formatTanggal(tanggalAwal)} s/d{" "}
            {formatTanggal(tanggalAkhir)}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Total: {detailData.length} laporan (Diurutkan berdasarkan tanggal)
          </MDTypography>
        </MDBox>

        {isMobile ? (
          <MDBox display="flex" flexDirection="column" gap={1}>
            {loading && (
              <MDTypography variant="button" color="text.secondary">
                Memuat...
              </MDTypography>
            )}
            {!loading &&
              detailData.map((row, index) => {
                const hari = formatDate(row.tanggal).split(",")[0];
                return (
                  <Card
                    key={`${row.id_laporan || row.tanggal || "row"}-${index}`}
                  >
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <MDTypography variant="button" fontWeight="bold">
                            {index + 1}. {formatTanggal(row.tanggal)} ({hari})
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDBox
                            mt={0.5}
                            display="flex"
                            alignItems="center"
                            gap={1}
                          >
                            <Chip
                              label={`Jumlah Kegiatan: ${
                                row.jumlah_kegiatan || 0
                              }`}
                              color="info"
                              size="small"
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12}>
                          <MDButton
                            variant="gradient"
                            color="info"
                            size="small"
                            onClick={() =>
                              handleOpenKegiatanModal(
                                row.id_laporan,
                                row.tanggal
                              )
                            }
                            sx={{ minWidth: "auto", px: 1 }}
                          >
                            <Icon sx={{ fontSize: 16 }}>visibility</Icon>
                            &nbsp;Lihat Kegiatan
                          </MDButton>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
          </MDBox>
        ) : (
          <DataTable
            table={{
              columns,
              rows: detailData,
            }}
            entriesPerPage={false}
            showTotalEntries={false}
            isSorted={false}
            noEndBorder
            loading={loading}
          />
        )}

        {detailData.length === 0 && !loading && (
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={4}
          >
            <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
              assessment
            </Icon>
            <MDTypography variant="h6" color="text.secondary">
              Tidak ada data laporan harian
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary">
              Coba periksa kembali periode yang dipilih
            </MDTypography>
          </MDBox>
        )}

        {loading && (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <MDTypography variant="h6" color="text.secondary">
              Memuat data...
            </MDTypography>
          </MDBox>
        )}

        {/* Modal Kegiatan */}
        <Dialog
          open={openKegiatanModal}
          onClose={handleCloseKegiatanModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">{selectedLaporanTitle}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {loadingKegiatan ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Memuat data kegiatan...
                  </MDTypography>
                </MDBox>
              ) : selectedKegiatan.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Tidak ada kegiatan untuk ditampilkan
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  <MDBox mb={3}>
                    <MDTypography variant="h6" color="info" gutterBottom>
                      Total Kegiatan: {selectedKegiatan.length}
                    </MDTypography>
                  </MDBox>
                  <Grid container spacing={2}>
                    {selectedKegiatan.map((kegiatan, index) => (
                      <Grid item xs={12} key={index}>
                        <Card sx={{ p: 2, mb: 2 }}>
                          <MDBox>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={2}
                            >
                              <MDTypography
                                variant="h6"
                                color="info"
                                gutterBottom
                              >
                                Kegiatan {index + 1}
                              </MDTypography>
                            </MDBox>

                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Deskripsi Kegiatan:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.kegiatan}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={12}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.output}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={6}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Jumlah Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.jumlah_output}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={6}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Satuan Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.satuan_output}
                                </MDTypography>
                              </Grid>
                            </Grid>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseKegiatanModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default PerorangDetail;
